import { useState } from 'react';
import { supabase } from '@/lib/supabase/client';
import { Instagram, Loader2, Check } from 'lucide-react';

interface AccessRequestFormProps {
  userId: string;
  phoneNumber: string;
  onComplete?: () => void;
}

export function AccessRequestForm({ userId, phoneNumber: _phoneNumber, onComplete }: AccessRequestFormProps) {
  const [fullName, setFullName] = useState('');
  const [instagram, setInstagram] = useState('');
  const [referralSource, setReferralSource] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    
    if (!fullName.trim()) {
      setError('Please enter your full name');
      return;
    }

    setSubmitting(true);
    
    try {
      // Update members with access request info (public.users dropped)
      const { error: updateError } = await supabase
        .from('members')
        .update({
          full_name: fullName.trim(),
          instagram_handle: instagram.trim() ? instagram.trim().replace('@', '') : null,
          approval_status: 'pending',
          updated_at: new Date().toISOString()
        })
        .eq('id', userId);

      if (updateError) {
        throw updateError;
      }

      onComplete?.();
    } catch (err: any) {
      console.error('[AccessRequestForm] Error:', err);
      setError(err.message || 'Failed to submit access request');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-md w-full mx-auto p-6">
      <div className="text-center mb-6">
        <div className="w-16 h-16 bg-olive/10 rounded-full flex items-center justify-center mx-auto mb-4">
          <Check className="w-8 h-8 text-olive" />
        </div>
        <h2 className="text-2xl font-light text-bone mb-2">Phone Verified!</h2>
        <p className="text-ash text-sm">Complete your access request to join Rendezvous</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label htmlFor="fullName" className="block text-sm text-ash mb-2">
            Full Name *
          </label>
          <input
            id="fullName"
            type="text"
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            placeholder="Jane Smith"
            className="w-full px-4 py-3 bg-charcoal border border-ash/30 rounded-lg text-bone 
                     placeholder:text-ash/50 focus:outline-none focus:border-olive transition-colors"
            disabled={submitting}
          />
        </div>

        <div>
          <label htmlFor="instagram" className="block text-sm text-ash mb-2">
            Instagram Handle (optional)
          </label>
          <div className="relative">
            <Instagram className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-ash/50" />
            <input
              id="instagram"
              type="text"
              value={instagram}
              onChange={(e) => setInstagram(e.target.value)}
              placeholder="@yourhandle"
              className="w-full pl-10 pr-4 py-3 bg-charcoal border border-ash/30 rounded-lg text-bone 
                       placeholder:text-ash/50 focus:outline-none focus:border-olive transition-colors"
              disabled={submitting}
            />
          </div>
        </div>

        <div>
          <label htmlFor="referral" className="block text-sm text-ash mb-2">
            How did you hear about Rendezvous? (optional)
          </label>
          <textarea
            id="referral"
            value={referralSource}
            onChange={(e) => setReferralSource(e.target.value)}
            placeholder="Friend referral, social media, etc."
            rows={3}
            className="w-full px-4 py-3 bg-charcoal border border-ash/30 rounded-lg text-bone 
                     placeholder:text-ash/50 focus:outline-none focus:border-olive transition-colors resize-none"
            disabled={submitting}
          />
        </div>

        {error && (
          <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm">
            {error}
          </div>
        )}

        <button
          type="submit"
          disabled={submitting || !fullName.trim()}
          className="w-full py-3 bg-olive text-bone rounded-lg font-medium hover:bg-olive/90 
                   transition-colors disabled:opacity-50 disabled:cursor-not-allowed
                   flex items-center justify-center gap-2"
        >
          {submitting ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" />
              Submitting...
            </>
          ) : (
            'Request Access'
          )}
        </button>

        <p className="text-center text-ash/70 text-xs mt-4">
          Our team will review your request. We're onboarding users in waves to ensure
          a high-quality experience as we refine our AI concierge in real-time.
        </p>
      </form>
    </div>
  );
}
